import { useDelete, useGet, useMutationWithErrorHandling, usePost, usePut, useQueryWithErrorHandling } from '@app/sdk';
import { useQueryClient } from '@tanstack/react-query';

export function useConsumerBillingService() {
  const get = useGet(true, 'ams');
  const post = usePost(true, 'ams');
  const put = usePut(true, 'ams');
  const sendDeleteRequest = useDelete();

  function getCurrentAccountPosition(accountId: string) {
    return get<any>(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/current-position`);
  }

  function getBillingPeriods(accountId: string) {
    return get<{
      value: BillingPeriod[];
    }>(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/billing-periods`).then(
      (response) => response.value
    );
  }

  function getCurrentBillingPeriod(accountId) {
    return get<BillingPeriod>(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/billing-periods/current`);
  }

  function requestStatementDayChange(accountId, requestedStatementDay) {
    return post<{ status: string; declineReasons?: any }>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/statement-day-change-requests`,
      {
        requestedStatementDay,
      }
    );
  }

  function getAlternateMinimumDueArrangements(accountId) {
    return get<{
      value: Arrangement[];
    }>(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/alternate-minimum-due-arrangements`).then(
      (response) => response.value
    );
  }

  function removeAlternateMinimumDueArrangement(accountId, arrangementId) {
    return sendDeleteRequest(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/alternate-minimum-due-arrangements/${arrangementId}`
    );
  }

  function updateAlternateMinimumDueArrangement(accountId, arrangementId, amount, startDate, endDate) {
    return put(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/alternate-minimum-due-arrangements/${arrangementId}`,
      {
        amount: {
          value: amount,
          currency: 'GBP',
        },
        startDate,
        endDate,
      }
    );
  }

  function getStatementDayChange(accountId: string) {
    return get<StatementDayChanges>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/statement-day-change-requests`
    ).then((response) => response.value);
  }

  function requestAlternateMinimumDue(accountId, amount, startDate, endDate) {
    return post(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/alternate-minimum-due-arrangements`, {
      amount: {
        value: amount,
        currency: 'GBP',
      },
      startDate,
      endDate,
    });
  }

  function getBillingPeriod(accountId, billingPeriodId) {
    return get<BillingPeriod>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/billing-periods/${billingPeriodId}`
    );
  }

  function getDuesAllocation(accountId: string, transactionId: string) {
    return get(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/transactions/${transactionId}/allocations`
    );
  }

  function getReageAdjustments(accountId: string) {
    return get<any>(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/current-position/reage-adjustments`);
  }

  function requestManualReage(accountId: string, reageType: string, reageStrategy: string, newArrearLevel: number) {
    const requestBody = {
      type: reageType,
    };

    if (reageType !== 'Full') {
      requestBody['partialReageParameters'] = {
        reageStrategy,
        newArrearLevel,
      };
    }

    return post(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/current-position/reage-adjustments`,
      requestBody
    );
  }

  function checkStatementDayChangeEligibility(accountId: string) {
    return get<StatementDayEligibilityResponse>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/statement-day-change-requests/eligibility`
    );
  }

  function getRecommendedExtraPaymentOptOutOptions(accountId: string) {
    return get<RecommendedExtraPaymentOptOutOptions>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/recommended-extra-payment-opt-out-options`
    );
  }

  function getRecommendedExtraPaymentOptOutStatus(accountId: string) {
    return get<RecommendedExtraPaymentOptOutStatus>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/recommended-extra-payment-opt-out`
    );
  }

  function setRecommendedExtraPaymentOptOutOptions(accountId, permanentOptOut, optOutBillingPeriods) {
    return post<{ permanentOptOut: boolean; optOutBillingPeriods?: number }>(
      `/api/proxy/ams/consumer-billing-service/accounts/${accountId}/recommended-extra-payment-opt-out`,
      {
        permanentOptOut,
        optOutBillingPeriods
      }
    );
  }

  function setRecommendedExtraPaymentOptIn(accountId: string) {
    return post(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/recommended-extra-payment-opt-in`, null);
  }

  function setRecommendedExtraPaymentOptOutCurrentBillingPeriodCharge(accountId: string) {
    return post(`/api/proxy/ams/consumer-billing-service/accounts/${accountId}/recommended-extra-payment-opt-out/current-billing-period`, null);
  }

  return {
    getCurrentAccountPosition,
    getBillingPeriods,
    getCurrentBillingPeriod,
    requestStatementDayChange,
    getAlternateMinimumDueArrangements,
    removeAlternateMinimumDueArrangement,
    updateAlternateMinimumDueArrangement,
    getStatementDayChange,
    requestAlternateMinimumDue,
    getBillingPeriod,
    getDuesAllocation,
    getReageAdjustments,
    requestManualReage,
    checkStatementDayChangeEligibility,
    getRecommendedExtraPaymentOptOutOptions,
    getRecommendedExtraPaymentOptOutStatus,
    setRecommendedExtraPaymentOptOutOptions,
    setRecommendedExtraPaymentOptIn,
    setRecommendedExtraPaymentOptOutCurrentBillingPeriodCharge
  };
}

export function useCurrentAccountPosition(accountId: string) {
  const { getCurrentAccountPosition } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['currentAccountPosition', accountId],
    queryFn: () => getCurrentAccountPosition(accountId),
  });
}

export function useGetBillingPeriods(accountId: string) {
  const { getBillingPeriods } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['billingPeriods', accountId],
    queryFn: () => getBillingPeriods(accountId),
    enabled: !!accountId,
  });
}

export function useCurrentBillingPeriod(accountId: string) {
  const { getCurrentBillingPeriod } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['currentBillingPeriod', accountId],
    queryFn: () => getCurrentBillingPeriod(accountId),
    enabled: !!accountId,
    silent404: true,
  });
}

export function useRefreshCurrentBillingPeriod() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['currentBillingPeriod'] });
}

export function useRequestStatementDayChange() {
  const { requestStatementDayChange } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({ accountId, requestedStatementDay }: { accountId: string; requestedStatementDay: number }) =>
      requestStatementDayChange(accountId, requestedStatementDay),
  });
}

export function useGetAlternateMinimumDueArrangements(accountId: string) {
  const { getAlternateMinimumDueArrangements } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['alternateMinimumDueArrangements', accountId],
    queryFn: () => getAlternateMinimumDueArrangements(accountId),
    enabled: !!accountId,
  });
}

export function useRefreshAlternateMinimumDueArrangements() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['alternateMinimumDueArrangements'] });
}

export function useRemoveAlternateMinimumDueArrangement() {
  const { removeAlternateMinimumDueArrangement } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({ accountId, arrangementId }: { accountId: string; arrangementId: string }) =>
      removeAlternateMinimumDueArrangement(accountId, arrangementId),
  });
}

export function useUpdateAlternateMinimumDueArrangement() {
  const { updateAlternateMinimumDueArrangement } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId,
      arrangementId,
      amount,
      startDate,
      endDate,
    }: {
      accountId: string;
      arrangementId: string;
      amount: number;
      startDate: string;
      endDate: string;
    }) => updateAlternateMinimumDueArrangement(accountId, arrangementId, amount, startDate, endDate),
  });
}

export function useStatementDayChange({
  accountId,
  predictor = () => true,
}: {
  accountId: string;
  predictor?: (item: StatementDayChange) => boolean;
}) {
  const { getStatementDayChange } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['statementDayChange', accountId],
    queryFn: () => getStatementDayChange(accountId).then((response) => response.filter(predictor)),
  });
}

export function useRefreshStatementDayChange() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['statementDayChange'] });
}

export function useRequestAlternateMinimumDue() {
  const { requestAlternateMinimumDue } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId,
      amount,
      startDate,
      endDate,
    }: {
      accountId: string;
      amount: number;
      startDate: string;
      endDate: string;
    }) => requestAlternateMinimumDue(accountId, amount, startDate, endDate),
  });
}

export function useGetBillingPeriod(accountId: string, billingPeriodId: number) {
  const { getBillingPeriod } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['billingPeriod', accountId, billingPeriodId],
    queryFn: () => getBillingPeriod(accountId, billingPeriodId),
    enabled: !!accountId && !!billingPeriodId,
    silent404: true,
  });
}

export function useGetReageAdjustments(accountId: string) {
  const { getReageAdjustments } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['reageAdjustments', accountId],
    queryFn: () => getReageAdjustments(accountId),
  });
}

export function useRequestManualReage() {
  const { requestManualReage } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId,
      reageType,
      reageStrategy,
      newArrearLevel,
    }: {
      accountId: string;
      reageType: string;
      reageStrategy: string;
      newArrearLevel: number;
    }) => requestManualReage(accountId, reageType, reageStrategy, newArrearLevel),
  });
}

export function useRefreshConsumerBillingService(accountId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['currentBillingPeriod'] }),
      queryClient.invalidateQueries({ queryKey: ['billingPeriods'] }),
      queryClient.invalidateQueries({ queryKey: ['alternateMinimumDueArrangements'] }),
      queryClient.invalidateQueries({ queryKey: ['statementDayChange'] }),
      queryClient.invalidateQueries({ queryKey: ['billingPeriod', accountId] }),
      queryClient.invalidateQueries({ queryKey: ['currentAccountPosition', accountId] }),
    ]);
}

export function useCheckStatementDayChangeEligibility(accountId: string) {
  const { checkStatementDayChangeEligibility } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['checkStatementDayChangeEligibility', accountId],
    queryFn: () => checkStatementDayChangeEligibility(accountId),
  });
}

export function useRefreshStatementDayChangeEligibility(accountId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['checkStatementDayChangeEligibility', accountId] });
}

export function useGetRecommendedExtraPaymentOptOutOptions(accountId: string) {
  const { getRecommendedExtraPaymentOptOutOptions } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['recommendedExtraPaymentOptOutOptions', accountId],
    queryFn: () => getRecommendedExtraPaymentOptOutOptions(accountId),
  });
}

export function useGetRecommendedExtraPaymentOptOutStatus(accountId: string) {
  const { getRecommendedExtraPaymentOptOutStatus } = useConsumerBillingService();
  return useQueryWithErrorHandling({
    queryKey: ['recommendedExtraPaymentOptOutStatus', accountId],
    queryFn: () => getRecommendedExtraPaymentOptOutStatus(accountId),
  });
}

export function useSetRecommendedExtraPaymentOptOutStatus() {
  const { setRecommendedExtraPaymentOptOutOptions } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId,
      permanentOptOut,
      optOutBillingPeriods
    }: {
      accountId: string;
      permanentOptOut: boolean;
      optOutBillingPeriods: number
    }) => setRecommendedExtraPaymentOptOutOptions(accountId, permanentOptOut, optOutBillingPeriods)
  });
}

export function useSetRecommendedExtraPaymentOptIn() {
  const { setRecommendedExtraPaymentOptIn } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId
    }: {
      accountId: string;
    }) => setRecommendedExtraPaymentOptIn(accountId)
  });
}

export function useOptOutOfCurrentBillingPeriodCharge() {
  const { setRecommendedExtraPaymentOptOutCurrentBillingPeriodCharge } = useConsumerBillingService();
  return useMutationWithErrorHandling({
    mutationFn: ({ accountId }: { accountId: string }) => setRecommendedExtraPaymentOptOutCurrentBillingPeriodCharge(accountId)
  });
}

export function useRefreshOptOutStatus() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['recommendedExtraPaymentOptOutStatus'] });
}